import {useQuery} from "@tanstack/react-query";
import vehiclesService from "../../../../services/web/vehicles-service.js";
import vmsService from "../../../../services/web/vms-service.js";
import vehicleClassesService from "../../../../services/web/vehicle-classes-service.js";
import ownerService from "../../../../services/web/owner-service.js";
import inspectionItemsService from "../../../../services/web/inspection-items-service.js";
import reviewItemsService from "../../../../services/web/review-items-service.js";

const STATIC_DATA_STALETIME = 10 * 60 * 1000;
const useVehiclesQueries = (id) => {
  const parsedId = id ? Number(id) : null;
  const isEnabled = !!parsedId;

  const {data: vehicleData = null, isLoading: isLoadingData} = useQuery({
    queryFn: () => vehiclesService.getById(parsedId),
    queryKey: ["vehicles", "detail", parsedId],
    enabled: isEnabled,
  });

  const {data: vehicleDamages = [], isLoading: isLoadingDamage} = useQuery({
    queryFn: () => vehiclesService.getVehicleDamage({id: parsedId}),
    queryKey: ["vehicles", "damages", parsedId],
    select: data => data.map(el =>
      el.checked ? {...el, dontShow: true} : el
    ),
    enabled: isEnabled
  });

  const { data: reviewItemsData = [], isLoading: isLoadingReviewItem} = useQuery({
    queryKey: ["vehicles", "review-items-form", parsedId],
    queryFn: async () => {
      const [reviewItemsRaw, reviewItemsValue] = await Promise.all([
        reviewItemsService.getAll(),
        parsedId ? vehiclesService.getReviewsItemsByVehicleId(parsedId) : Promise.resolve([])
      ]);

      let reviewItemsBasicFormArr = reviewItemsRaw.map(el => ({
        description_id: el.id,
        description: el.description,
        id: "",
        km: "",
        date: null,
        vehicle_id: "",
        next_km_revision: "",
        next_date_revision: null,
        applyEffect: false
      }));

      if (id && reviewItemsValue.length > 0) {
        reviewItemsBasicFormArr = reviewItemsBasicFormArr.map(item => {
          const saved = reviewItemsValue.find(
            el => el.description_id === item.description_id || el.id === item.description_id
          );

          if (!saved) return item;

          return {
            ...item,
            id: saved.id,
            km: saved.km,
            date: saved.date,
            vehicle_id: saved.vehicle_id,
            next_km_revision: saved.next_km_revision,
            next_date_revision: saved.next_date_revision,
            applyEffect: true
          };
        });
      }
      return reviewItemsBasicFormArr;
    },
  });

  const {data: vehicleClasses = [], isLoading: isLoadingVehicleClasses} = useQuery({
    queryFn: vehicleClassesService.getAllToSelect,
    queryKey: ["classes"],
    staleTime: STATIC_DATA_STALETIME,
    select: data => data.map(el => ({
      value: el.id,
      label: el.text
    }))
  });

  const {data: owners = [], isLoading: isLoadingOwners} = useQuery({
    queryFn: ownerService.getAllToSelect,
    queryKey: ["owners"],
    staleTime: STATIC_DATA_STALETIME,
    select: data => data.map(el => ({
      value: el.id,
      label: el.text
    }))
  });

  const {data: inspectionsItems = [], isLoading: isLoadingInspectionsItems} = useQuery({
    queryFn: inspectionItemsService.getAllToSelect,
    queryKey: ["inspectionItems"],
    staleTime: STATIC_DATA_STALETIME,
    select: data => data.map(el => ({
      value: el.id,
      label: el.text
    }))
  });

  const {data: cameras = [], isLoading: isLoadingCameras} = useQuery({
    queryFn: vmsService.list,
    queryKey: ["cameras"],
    staleTime: STATIC_DATA_STALETIME,
  });

  const isLoading = isLoadingCameras
    || isLoadingOwners
    || isLoadingData
    || isLoadingDamage
    || isLoadingVehicleClasses
    || isLoadingInspectionsItems
    || isLoadingReviewItem

  return {
    vehicleData,
    vehicleDamages,
    reviewItemsData,
    vehicleClasses,
    owners,
    inspectionsItems,
    cameras,
    isLoading,
  }
}

export default useVehiclesQueries;